import { Box, Button, Grid } from "@material-ui/core";
import CountTextField from "@saleor/components/CountTextField";
import DatePicker from "@saleor/components/DatePicker";
import MyTypography from "@saleor/components/MyTypography";
import SingleSelectField from "@saleor/components/SingleSelectField";
import SingleTextField from "@saleor/components/SingleTextField";
import classNames from "classnames";
import React, { FC, memo, useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import ProductInfoIcon from "../../../../../assets/images/clipboard-list.svg";
import CommentCustomerIcon from "../../../../../assets/images/comment-customer.svg";
import OfficeIcon from "../../../../../assets/images/office-building.svg";
import LogisticIcon from "../../../../../assets/images/truck.svg";
import { CustomerContext } from "../CollectionFormSubmit";
import Shipping from "./Shipping";
import useStyles from "./styles";

interface IAdditionalInfoRender {
  id: number;
  label: string;
  name: string;
  placeholder: string;
  icon?: React.ReactNode;
}

const CustomerData: FC = memo(() => {
  const { formik, customerData } = useContext(CustomerContext);
  const classes = useStyles();
  const intl = useIntl();

  const orderTypeChoices = [
    {
      label: (
        <span>
          <span className={classes.selectItem}>ZOR</span>
          {`(Standard Order)`}
        </span>
      ),
      value: "ZOR"
    },
    {
      label: (
        <span>
          <span className={classes.selectItem}>ZBV</span>
          {`(Cash Sale)`}
        </span>
      ),
      value: "ZBV"
    }
  ];

  const additionalInfoRender: IAdditionalInfoRender[] = [
    {
      id: 1,
      label: intl.formatMessage({
        defaultMessage: "บันทึกภายในถึงคลังสินค้า",
        description: "label"
      }),
      name: "internalCommentsToWarehouse",
      placeholder: "พิมพ์ที่นี่...",
      icon: <img src={OfficeIcon} alt="" />
    },
    {
      id: 2,
      label: intl.formatMessage({
        defaultMessage: "ข้อมูลเกี่ยวกับการสั่งผลิต",
        description: "label"
      }),
      name: "productInformation",
      placeholder: "พิมพ์ที่นี่...",
      icon: <img src={ProductInfoIcon} alt="" />
    },
    {
      id: 3,
      label: intl.formatMessage({
        defaultMessage: "บันทึกภายในเกี่ยวกับการจัดส่ง",
        description: "label"
      }),
      name: "internalCommentsToLogistic",
      placeholder: "พิมพ์ที่นี่...",
      icon: <img src={LogisticIcon} alt="" />
    },
    {
      id: 4,
      label: intl.formatMessage({
        defaultMessage: "ข้อความสำหรับแจ้งลูกค้า",
        description: "label"
      }),
      name: "externalCommentsToCustomer",
      placeholder: "พิมพ์ที่นี่...",
      icon: <img src={CommentCustomerIcon} alt="" />
    }
  ];

  return (
    <Box className={classes.root}>
      <div className={classes.header}>
        <MyTypography variant="subtitle1" className={classes.titleHeader}>
          ข้อมูลเกี่ยวกับลูกค้า
        </MyTypography>
      </div>
      <div className={classes.wrapContent}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <div className={classes.customerBox}>
              <MyTypography variant="subtitle2" className={classes.title}>
                {intl.formatMessage({
                  defaultMessage: "Sold To",
                  description: "label"
                })}
              </MyTypography>
              <div className={classes.boxInfo}>
                <MyTypography variant="body3" className={classes.labelAddress}>
                  {intl.formatMessage({
                    defaultMessage: "รหัสลูกค้า",
                    description: "label"
                  })}
                </MyTypography>
                <MyTypography
                  variant="subtitle2"
                  className={classes.labelAddress}
                >
                  {customerData?.customerInfo?.code}
                </MyTypography>
              </div>
              <div className={classes.boxInfo}>
                <MyTypography variant="body3" className={classes.labelAddress}>
                  {intl.formatMessage({
                    defaultMessage: "ชื่อลูกค้า",
                    description: "label"
                  })}
                </MyTypography>
                <MyTypography variant="body2" className={classes.labelAddress}>
                  {customerData?.customerInfo?.name}
                </MyTypography>
              </div>
            </div>
            <div className={classes.customerInput}>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <MyTypography
                    variant="subtitle3"
                    className={classes.inputLabel}
                  >
                    {intl.formatMessage({
                      defaultMessage: "วันที่ออก PO",
                      description: "label"
                    })}
                  </MyTypography>
                  <div className={classes.inputField}>
                    <DatePicker
                      name={"poDate"}
                      value={formik.values.poDate}
                      onChange={formik.handleChange}
                      error={!!(formik.errors.poDate && formik.touched.poDate)}
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <div className={classes.inputField}>
                    <MyTypography
                      variant="subtitle3"
                      className={classes.inputLabel}
                    >
                      {intl.formatMessage({
                        defaultMessage: "เลขที่ PO",
                        description: "label"
                      })}
                      <span className={classes.required}>*</span>
                    </MyTypography>
                    <SingleTextField
                      onChange={formik.handleChange}
                      value={formik.values.poNumber}
                      name={"poNumber"}
                      placeholder={"ระบุเลขที่ PO"}
                      error={
                        !!(formik.errors.poNumber && formik.touched.poNumber)
                      }
                      className={classes.textFieldNoLabel}
                      maxLengthProps={30}
                    />
                  </div>
                </Grid>
              </Grid>
              <Grid container spacing={4}>
                <Grid item xs={12} md={6}>
                  <MyTypography
                    variant="subtitle3"
                    className={classNames(classes.inputLabel)}
                  >
                    {intl.formatMessage({
                      defaultMessage: "วันที่ต้องการสินค้า",
                      description: "label"
                    })}
                    <span className={classes.required}>*</span>
                  </MyTypography>
                  <div className={classes.inputField}>
                    <DatePicker
                      name={"requestDate"}
                      value={formik.values.requestDate}
                      onChange={formik.handleChange}
                      error={
                        !!(
                          formik.errors.requestDate &&
                          formik.touched.requestDate
                        )
                      }
                    />
                  </div>
                </Grid>
                <Grid item xs={12} md={6}>
                  <MyTypography
                    variant="subtitle3"
                    className={classNames(classes.inputLabel)}
                  >
                    {intl.formatMessage({
                      defaultMessage: "Order Type",
                      description: "label"
                    })}
                    <span className={classes.required}>*</span>
                  </MyTypography>
                  <div className={classes.inputField}>
                    <SingleSelectField
                      className={classes.selectField}
                      onChange={formik.handleChange}
                      choices={orderTypeChoices}
                      value={formik.values.orderType}
                      name={"orderType"}
                      error={
                        !!(formik.errors.orderType && formik.touched.orderType)
                      }
                      placeholder={"เลือก Order Type"}
                    />
                  </div>
                </Grid>
              </Grid>
              <Shipping
                address={
                  customerData?.customerInfo?.representatives[0]?.addresses
                }
              />
            </div>
          </Grid>
          <Grid item xs={12} md={6}>
            <div className={classes.wrapInfoMore}>
              <div className={classes.title}>
                <MyTypography
                  variant={"body2"}
                  className={classes.titleInfoMore}
                >
                  <FormattedMessage
                    defaultMessage={"บันทึกภายในและข้อมูลเพิ่มเติม"}
                  />
                </MyTypography>
                {additionalInfoRender.map(
                  (infoRender: IAdditionalInfoRender) => (
                    <CountTextField
                      key={infoRender.id}
                      maxLength={250}
                      title={
                        <div className={classes.titleTextArea}>
                          {infoRender.icon}
                          <MyTypography
                            variant="body3"
                            className={classes.subTitleTextArea}
                          >
                            {infoRender.label}
                          </MyTypography>
                        </div>
                      }
                      name={infoRender.name}
                      placeHolder={infoRender.placeholder}
                      onChange={e =>
                        formik.setFieldValue(infoRender.name, e.target.value)
                      }
                      value={formik.values[infoRender.name]}
                    />
                  )
                )}
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
      <div className={classes.footer}>
        <Button
          variant={"contained"}
          type={"submit"}
          className={classNames(classes.buttonApply, "defaultB")}
        >
          {intl.formatMessage({
            defaultMessage: "APPLY ALL",
            description: "button label"
          })}
        </Button>
      </div>
    </Box>
  );
});

export default CustomerData;
